import React from "react";
import { ArrowLeft } from "lucide-react";
import { LogoFilterDefs } from "../components/landing/Logo";

/**
 * Vexly One Support — public support page for App Store / Google Play.
 * Pure text content, bilingual (ES then EN).
 * Route: /one/soporte
 */
export default function VexlyOneSupport() {
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white antialiased">
      <LogoFilterDefs />

      <header className="border-b border-white/5">
        <div className="max-w-[760px] mx-auto px-5 md:px-8 h-16 flex items-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={15} />
            vexlyapp.com
          </a>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-16 text-[15px] md:text-base leading-[1.75] text-white/80">
        {/* ===== Español ===== */}
        <article lang="es">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-2">
            Soporte de Vexly One
          </h1>
          <p className="mb-10 text-white/75">
            ¿Necesitas ayuda con Vexly One? Escríbenos a{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="support-email-link-es"
            >
              soporte@vexlyapp.com
            </a>{" "}
            desde el correo de tu negocio e indica el nombre de tu empresa y
            tu usuario.
          </p>

          <Section title="Preguntas frecuentes">
            <ul className="list-disc pl-6 space-y-3 marker:text-brand-orange/70">
              <li>
                <strong className="text-white font-medium">
                  ¿Cómo elimino mi cuenta?
                </strong>{" "}
                En la app: Ajustes → Eliminar mi cuenta. También puedes
                pedirlo en{" "}
                <a
                  href="/one/eliminar-cuenta"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-delete-link-es"
                >
                  vexlyapp.com/one/eliminar-cuenta
                </a>
                .
              </li>
              <li>
                <strong className="text-white font-medium">
                  ¿Qué pasa con mis facturas si elimino la cuenta?
                </strong>{" "}
                Los comprobantes fiscales se conservan 10 años porque la ley
                tributaria dominicana lo exige. Lee la{" "}
                <a
                  href="/one/privacidad"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-privacy-link-es"
                >
                  Política de privacidad
                </a>
                .
              </li>
              <li>
                Política de privacidad:{" "}
                <a
                  href="/one/privacidad"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-privacy-url-es"
                >
                  vexlyapp.com/one/privacidad
                </a>
              </li>
            </ul>
          </Section>

          <p className="text-sm text-white/50">
            Vexly One es un producto de BRIGHTWAY SOLUTIONS LLC.
          </p>
        </article>

        <hr className="my-16 border-white/10" />

        {/* ===== English ===== */}
        <article lang="en">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-2">
            Vexly One Support
          </h1>
          <p className="mb-10 text-white/75">
            Need help with Vexly One? Email{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="support-email-link-en"
            >
              soporte@vexlyapp.com
            </a>{" "}
            from your business email and include your company name and
            username.
          </p>

          <Section title="FAQ">
            <ul className="list-disc pl-6 space-y-3 marker:text-brand-orange/70">
              <li>
                <strong className="text-white font-medium">
                  How do I delete my account?
                </strong>{" "}
                In the app: Settings → Delete my account. You can also request
                it at{" "}
                <a
                  href="/one/eliminar-cuenta"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-delete-link-en"
                >
                  vexlyapp.com/one/eliminar-cuenta
                </a>
                .
              </li>
              <li>
                <strong className="text-white font-medium">
                  What happens to my invoices if I delete my account?
                </strong>{" "}
                Tax receipts are kept for 10 years as required by Dominican
                tax law. See the{" "}
                <a
                  href="/one/privacidad"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-privacy-link-en"
                >
                  Privacy Policy
                </a>
                .
              </li>
              <li>
                Privacy Policy:{" "}
                <a
                  href="/one/privacidad"
                  className="text-brand-orange hover:text-brand-orangeHover underline-grow"
                  data-testid="support-privacy-url-en"
                >
                  vexlyapp.com/one/privacidad
                </a>
              </li>
            </ul>
          </Section>

          <p className="text-sm text-white/50">
            Vexly One is a product of BRIGHTWAY SOLUTIONS LLC.
          </p>
        </article>

        <footer className="mt-16 pt-6 border-t border-white/5 text-xs text-white/40">
          <p>
            © 2026 BRIGHTWAY SOLUTIONS LLC. All rights reserved. ·{" "}
            <a href="/" className="hover:text-white underline-grow">
              vexlyapp.com
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-3 tracking-tight">
        {title}
      </h2>
      <div className="text-white/75">{children}</div>
    </section>
  );
}
